// shared-lib/cabinet-runs/vanityCounterFollow.js
//
// What hangs from a vanity's COUNTER follows it up and down.
//
// `vanityBasinSeat.js` seats the basin on the counter a re-solve just built.
// Nothing moved the rest of the group with it: the faucet on the deck, the
// mirror hung a set distance above it and the lights over the mirror stayed
// at the height of the counter that was there before. A vanity run changes
// its counter height whenever its body height changes — a family switch
// (Venus builds a 34.5" body, every other family the profile's 32",
// `runBodyHeight`), or the first fill of a vanity the builder made at another
// height — and the reported symptom was a faucet floating 1.85" over a
// generic counter and sunk 0.65" into a Venus one (2026-11-02).
//
// The rule is the counter's change in height, applied to the group's faucets,
// mirrors and wall lights (sconces and the bar over the mirror). Measured from
// the COUNTER, not from the basin: a basin can be redrawn at another size on
// the same counter (`vanityBasinSeat.refittedBasin`), and its seat then moves
// with nothing above it having any reason to.
//
// What RESTS on the counter moves too: Stage It parents its counter props to
// the slab (a soap, a tray, a plant), and stacks more on those (a candle on
// the tray). They belong to no vanity group, so the group rule never saw
// them; a repair of the vanities built before 2026-11-02 left them 1.9"
// over the lowered counter, and a family switch to Venus would sink them
// 2.5" into it. Measured from the slab each one named BEFORE the solve —
// `reparentOntoCountertops` has already pointed it at the new one — and a
// prop standing on another prop moves with it. Only objects in no fixture
// group: the vanity's own parts are parented in a chain (counter → sink →
// faucet → mirror → light) that would otherwise carry a pendant hung over
// the bowl down with the counter.
//
// Left alone:
//   - a ceiling light: it hangs from the ceiling;
//   - anything the run owns (`runId`): the fill rebuilt it at the new height
//     already — a drawn run's own faucet is re-emitted on its basin;
//   - anything this solve already moved up or down, so nothing moves twice.
//
// Pure. No I/O.

const { profileOf, VANITY_PROFILE_ID } = require('./profiles');
const { basinBaysFor, nearestCounter, SEAT_EPS } = require('./vanityBasinSeat');
const {
  computeCountertopTopY,
  getFixtureGroupFaucets,
  getFixtureGroupMirrors,
  getFixtureGroupOverheadLights,
  isSconce,
} = require('../vanity-assembly/fixtureGroup');
const {
  findKitchenCountertopFor,
} = require('../kitchen-assembly/fixtureGroup');

const topOf = (counter) => counter.center.y + counter.dimensions.y / 2;

/**
 * How far one group's counter moved up (+) or down (-) in this solve, or 0.
 * `basins` are the group's bowls as they stand now; the counter each sat on
 * BEFORE is found through the prior basin in `priorObjects`, the way every
 * sink reader finds it (`findKitchenCountertopFor`: its `parents`, then its
 * group). The counter it sits on now is the run's nearest, as the seat reads
 * it.
 */
function counterShift(basins, priorObjects, priorById, counters) {
  for (const basin of basins) {
    const prior = priorById.get(basin.id);
    if (!prior?.center) continue;
    const before = computeCountertopTopY(
      findKitchenCountertopFor(priorObjects, prior),
      null
    );
    const now = nearestCounter(counters, basin.center || {});
    if (before == null || !now) continue;
    return topOf(now) - before;
  }
  return 0;
}

/** The group's members that hang from its counter, as `followCounterHeight` moves them. */
function counterFollowers(objects, groupId) {
  return [
    ...getFixtureGroupFaucets(objects, groupId),
    ...getFixtureGroupMirrors(objects, groupId),
    ...getFixtureGroupOverheadLights(objects, groupId),
    ...objects.filter((o) => o?.fixtureGroupId === groupId && isSconce(o)),
  ];
}

const parentsOf = (o) => (Array.isArray(o?.parents) ? o.parents : []);

/**
 * What rests on one of the run's `counters`, with how far each must move up
 * (+) or down (-): an object naming one of them as its countertop parent
 * moves by that slab's top against the top of the slab it named in
 * `priorById`; an object standing on a mover (a candle on a tray) moves with
 * it.
 *
 * Only objects in NO fixture group. A vanity's own parts are chained by
 * `parents` too — counter → sink → faucet → mirror → light, and a pendant
 * over the bowl names the sink — so walking that chain would carry a
 * ceiling light down with the counter. The group's parts follow the group
 * rule (`counterFollowers`), which knows what hangs from the ceiling.
 * Run-owned objects are the fill's, and are left out too.
 */
function restingShifts(list, priorById, counters) {
  const counterById = new Map(counters.map((c) => [c.id, c]));
  const loose = (o) => !!o?.id && !o.runId && !o.fixtureGroupId;
  const shift = new Map();
  for (const o of list) {
    if (!loose(o)) continue;
    const now = parentsOf(o).find(
      (p) => p?.type === 'countertop' && counterById.has(p.id)
    );
    if (!now) continue;
    const was = parentsOf(priorById.get(o.id)).find(
      (p) => p?.type === 'countertop'
    );
    const before = was && priorById.get(was.id);
    if (!before?.center || !before.dimensions) continue;
    const dy = topOf(counterById.get(now.id)) - topOf(before);
    if (Math.abs(dy) > SEAT_EPS) shift.set(o.id, dy);
  }
  // Each pass adds at least one object or ends, so it ends.
  for (let grew = true; grew; ) {
    grew = false;
    for (const o of list) {
      if (!loose(o) || shift.has(o.id)) continue;
      const on = parentsOf(o).find((p) => shift.has(p?.id));
      if (!on) continue;
      shift.set(o.id, shift.get(on.id));
      grew = true;
    }
  }
  return shift;
}

/**
 * `objects` with each vanity group this run hosts carried up or down by its
 * counter's change in height, and with what rests on the run's counters.
 *
 * @param {Object[]} objects       the merged room objects, basins already seated
 * @param {Object[]} priorObjects  the room's objects BEFORE the solve; without
 *   them there is nothing to measure a change from and nothing moves
 * @param {Object} run             the rehydrated run
 * @param {Object} [ctx]           `{ now, step }`, as the basin seat takes
 * @returns {{objects: Object[], moved: string[]}} the input array by IDENTITY
 *   when nothing moved, so a caller can skip a write.
 */
function followCounterHeight(objects, priorObjects, run, ctx = {}) {
  const list = Array.isArray(objects) ? objects : [];
  const none = { objects: list, moved: [] };
  if (profileOf(run).id !== VANITY_PROFILE_ID || !priorObjects?.length) {
    return none;
  }
  const counters = list.filter(
    (o) => o?.type === 'countertop' && o.runId === run.id
  );
  if (!counters.length) return none;
  const hosted = new Set(basinBaysFor(run, list).keys());
  const basinsByGroup = new Map();
  for (const o of list) {
    if (!o?.id || !hosted.has(o.id) || !o.fixtureGroupId) continue;
    if (!basinsByGroup.has(o.fixtureGroupId)) {
      basinsByGroup.set(o.fixtureGroupId, []);
    }
    basinsByGroup.get(o.fixtureGroupId).push(o);
  }

  const priorById = new Map(
    priorObjects.filter((o) => o?.id).map((o) => [o.id, o])
  );
  // Only what stood where it stood before: the run's own objects were
  // rebuilt at the new height, and anything this solve already moved up or
  // down (the basin, by its seat) must not move twice.
  const unmoved = (o) => {
    const prior = priorById.get(o.id);
    return (
      !o.runId &&
      !!prior?.center &&
      !!o.center &&
      Math.abs(o.center.y - prior.center.y) <= SEAT_EPS
    );
  };
  const shiftFor = new Map(); // object id → metres to move up
  for (const [groupId, basins] of basinsByGroup) {
    const dy = counterShift(basins, priorObjects, priorById, counters);
    if (Math.abs(dy) <= SEAT_EPS) continue;
    for (const o of counterFollowers(list, groupId)) {
      if (unmoved(o)) shiftFor.set(o.id, dy);
    }
  }
  const byId = new Map(list.filter((o) => o?.id).map((o) => [o.id, o]));
  for (const [id, dy] of restingShifts(list, priorById, counters)) {
    if (!shiftFor.has(id) && unmoved(byId.get(id))) shiftFor.set(id, dy);
  }
  if (!shiftFor.size) return none;

  const now = ctx.now || new Date().toISOString();
  const next = list.map((o) => {
    const dy = o?.id ? shiftFor.get(o.id) : undefined;
    if (dy === undefined) return o;
    const out = {
      ...o,
      center: { ...o.center, y: o.center.y + dy },
      lastModified: now,
    };
    if (run.source === 'scan') {
      out.scanAdjusted = [
        ...(Array.isArray(o.scanAdjusted) ? o.scanAdjusted : []),
        { step: ctx.step || 'run-bay', kind: 'followed-counter', deltaY: dy },
      ];
    }
    return out;
  });
  return { objects: next, moved: [...shiftFor.keys()] };
}

module.exports = { followCounterHeight, counterShift };
